"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SubQuote = exports.EqipQuote = exports.EqipCart = exports.EqipOrder = void 0;
const model_core_1 = require("@nxt/model-core");
const index_1 = require("../index");
const exclude = ['amount_paid', 'amount_refunded', 'num_delivered', 'num_shipped', 'replacement', 'selected_rate', 'shipping', 'total_weight'];
class EqipOrder extends model_core_1.Order {
    subquotes = new model_core_1.ChildArray();
    data = {};
    production_details = {};
    metadata = {};
    itemsSaved = false;
    constructor(data, olMap, type, idLen) {
        super(data, olMap || index_1.olm, type || 'orders', idLen || 26);
        (0, model_core_1.setObjectProperties)(this, data, olMap || index_1.olm, EqipOrder);
        this._s = [...this._s, 'items', 'stages', 'subquotes', 'metadata', 'production_details', 'paid', 'service_fee', 'balance'];
    }
    async loadAll(opts, stack, p) {
        opts = opts || {};
        opts.loadAllFn = opts.loadAllFn || index_1.loadAll;
        opts.olm = opts.olm || index_1.olm;
        await super.loadAll(opts, stack, p);
    }
    get balance() {
        let b = (0, model_core_1.round)((this.service_fee || 0) - (this.amount_paid || 0) + (this.amount_refunded || 0), 2);
        return b < 0 ? 0 : b;
    }
    get _payment_balance() {
        return (0, model_core_1.round)((this.service_fee || 0) - (this._payment_amounts || 0), 2);
    }
    add(p, i) {
        if (p === 'items') {
            if (!this.items.find(i => i.sku === i.sku)) {
                i.quantity = i.quantity || 1;
                this.items.push(i);
                if (this._docRef) {
                    i.save(this);
                    this.save();
                }
            }
        }
        else {
            super.add(p, i);
        }
    }
    async save(parent) {
        if (!parent && !this._docRef) {
            throw new Error(`${this._type} save failed: Parent or document reference is required.`);
        }
        if (!this._docRef) {
            this.setID();
            this._docRef = parent._docRef.collection(this._type).doc(this.id);
        }
        if (this.subquotes && this.subquotes.length > 0) {
            const subQuotesCollection = this._docRef.collection('subquotes');
            for (const subQuote of this.subquotes) {
                if (!subQuote._docRef) {
                    subQuote._docRef = subQuotesCollection.doc(subQuote.id);
                }
                await subQuote.save(this);
                if (subQuote.items?.length > 0) {
                    const itemPromises = subQuote.items.map(async (item) => {
                        return item.save(subQuote);
                    });
                    await Promise.all(itemPromises);
                }
                if (subQuote.files?.length > 0) {
                    const filePromises = subQuote.files.map(async (file) => {
                        return file.save(subQuote);
                    });
                    await Promise.all(filePromises);
                }
            }
        }
        if (this.items && this.items.length > 0) {
            const itemsCollection = this._docRef.collection('items');
            const itemPromises = this.items.map(async (item) => {
                if (!item._docRef) {
                    item._docRef = itemsCollection.doc();
                }
                return item.save(this);
            });
            await Promise.all(itemPromises);
        }
        await super.save(parent);
    }
    async delete() {
        if (!this._docRef) {
            throw `Cannot delete ${this._type}! No document reference.`;
        }
        try {
            if (this.subquotes && this.subquotes.length) {
                for (const subquote of this.subquotes) {
                    await subquote.loadAll();
                    if (subquote.items?.length) {
                        for (const item of subquote.items) {
                            await item.delete();
                        }
                    }
                    if (subquote.files?.length) {
                        for (const file of subquote.files) {
                            await file.delete();
                        }
                    }
                    await subquote.delete();
                }
            }
            if (this.items && this.items?.length) {
                for (const item of this.items) {
                    await item.delete();
                }
            }
            if (this.files && this.files?.length) {
                for (const file of this.files) {
                    await file.delete();
                }
            }
            await super.delete();
        }
        catch (error) {
            console.error(`Error deleting ${this._type}:`, error);
            throw new Error(`Failed to delete ${this._type}.`);
        }
    }
    async clearItems() {
        if (!this._exists)
            throw new Error("Quote doesn't exist.");
        try {
            const itemsCollectionRef = this._docRef.collection('items');
            const snapshot = await itemsCollectionRef.get();
            const deletePromises = snapshot.docs.map(doc => itemsCollectionRef.doc(doc.id).delete());
            await Promise.all(deletePromises);
        }
        catch (error) {
            console.error("Error clearing items:", error);
            throw new Error("Failed to clear items.");
        }
    }
    async addItems(items) {
        if (!this._exists)
            throw new Error("Quote doesn't exist.");
        try {
            const itemsCollectionRef = this._docRef.collection('items');
            const addPromises = items.map(item => {
                const newItemRef = itemsCollectionRef.doc();
                return newItemRef.set(item);
            });
            await Promise.all(addPromises);
        }
        catch (error) {
            console.error("Error adding items:", error);
            throw new Error("Failed to add items.");
        }
    }
    async updateItems() {
        try {
            if (!this._exists) {
                throw new Error("Order doesn't exist. Cannot update items.");
            }
            const itemsCollection = this._docRef.collection('items');
            const savePromises = this.items.map(item => {
                if (!item._docRef) {
                    item._docRef = itemsCollection.doc();
                }
                return item.save(this);
            });
            await Promise.all(savePromises);
            await this.calc();
            await this.save();
            console.log("Items updated and saved successfully.");
        }
        catch (error) {
            console.error("Failed to update items:", error);
            throw new Error("Failed to update items.");
        }
    }
    //Was not needed but will leave for now
    updateFrom(data) {
        for (const [key, value] of Object.entries(data)) {
            if (this.hasOwnProperty(key)) {
                const descriptor = Object.getOwnPropertyDescriptor(this, key);
                if (descriptor?.writable || descriptor?.set) {
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        if (typeof this[key] === 'object' && this[key] !== null) {
                            Object.assign(this[key], value);
                        }
                        else {
                            this[key] = { ...value };
                        }
                    }
                    else {
                        this[key] = value;
                    }
                }
            }
        }
    }
    calcTotals(u) {
        super.calcTotals(u);
        this.service_fee = (0, model_core_1.round)((this.total || 0) * 0.05, 2);
    }
    toJSON() {
        const r = super.toJSON();
        r.service_fee = this.service_fee;
        return r;
    }
}
exports.EqipOrder = EqipOrder;
class EqipCart extends EqipOrder {
    constructor(data, olMap) {
        super(data, olMap);
        (0, model_core_1.setObjectProperties)(this, data, olMap || index_1.olm, EqipCart);
        this.excludeProperties();
    }
    excludeProperties() {
        exclude.forEach((prop) => {
            delete this[prop];
        });
    }
    async copyToOrder(parent, keep) {
        await this.loadAll();
        return new EqipOrder(await super.clean(new EqipOrder(this), parent, keep || ['items'], null, true));
    }
}
exports.EqipCart = EqipCart;
class EqipQuote extends EqipOrder {
    constructor(data, olMap) {
        super(data, olMap, 'quotes', 26);
        (0, model_core_1.setObjectProperties)(this, data, olMap || index_1.olm, EqipQuote);
        this._s = [...this._s, 'items', 'stages', 'subQuotes'];
    }
    async copy(parent) {
        return new EqipQuote(await super.clean(new EqipQuote(this), parent, ['items', 'subQuotes'], null, true));
    }
    async copyToOrder(parent) {
        await this.loadAll();
        return new EqipOrder(await super.clean(new EqipOrder(this), parent, ['items', 'subQuotes'], null, true));
    }
    toString() {
        return `Quote: ${this.quote_id}`;
    }
    async updateQuote(changes) {
        if (!this._exists)
            throw new Error("Quote doesn't exist.");
        await this._docRef.update(changes);
    }
    async initializeQuote(quoteId) {
        this._docRef = this.fs.collection('quotes').doc(quoteId);
        const doc = await this._docRef.get();
        if (!doc.exists) {
            throw new Error(`No quote found with id: ${quoteId}`);
        }
        this._exists = true;
    }
}
exports.EqipQuote = EqipQuote;
class SubQuote extends EqipOrder {
    constructor(data, olMap) {
        super(data, olMap, 'subquotes', 26);
        (0, model_core_1.setObjectProperties)(this, data, olMap || index_1.olm, SubQuote);
        this._s = [...this._s, 'items', 'stages', 'subquotes'];
    }
    async copy(parent) {
        return new SubQuote(await super.clean(new SubQuote(this), parent, ['items', 'subquotes'], null, true));
    }
    async copyToOrder(parent) {
        await this.loadAll();
        return new EqipOrder(await super.clean(new EqipOrder(this), parent, ['items', 'subquotes'], null, true));
    }
    toString() {
        return `Quote: ${this.quote_id}`;
    }
}
exports.SubQuote = SubQuote;
index_1.olm.orders = (ref, map) => {
    return new EqipOrder(ref, map);
};
index_1.olm.quotes = (ref, map) => {
    return new EqipQuote(ref, map);
};
index_1.olm.subquotes = (ref, map) => {
    return new SubQuote(ref, map);
};
